import React, { useContext } from 'react'

import { Box, DisplayPosition } from '@gousto-internal/citrus-react'
import { useDispatch } from 'react-redux'

import { FlexPortionTileBadge, FlexPortionTileEditor } from '@features/recipe-tile'
import type { RecipeGridContentBlock } from '@library/api-menu-experience'
import { RecipeGrid } from '@library/menu-page'
import type { RecipeUUID } from '@library/menu-service'

import {
  WantToSeeMore,
  getClickAllRecipesTrackingAction,
} from 'routes/Menu/MenuRecipesPage/CTAToAllRecipes'
import {
  CaloriesTutorial,
  useShouldShowCaloriesTutorial,
} from 'routes/Menu/components/MenuPreferences'
import { CollectionId, CollectionSlug } from 'routes/Menu/domains/collections'
import { useSetBrowserCTAVisibility } from 'routes/Menu/domains/menu'

import { MenuBrandingContext } from '../menuBrandingContext'
import { useMenuExperienceCategoryNavigation } from '../menuExperienceNavigationContext'
import { useMenuExperienceRecipeDetails } from '../menuExperienceRecipeDetailContext'
import { useRecipeGridBasket } from './useRecipeGridBasket'
import { useRecipeGridCustomisationData } from './useRecipeGridCustomisationData'
import { useRecipeGridCustomisationEducation } from './useRecipeGridCustomisationEducation'
import { useRecipeGridFlex } from './useRecipeGridFlex'
import { useRecipeGridFlexEducation } from './useRecipeGridFlexEducation'

type RecipeGridContentBlockWrapperProps = {
  contentBlock: RecipeGridContentBlock
  collectionId?: string
}

export const RecipeGridContentBlockWrapper = ({
  contentBlock,
  collectionId,
}: RecipeGridContentBlockWrapperProps) => {
  const dispatch = useDispatch()
  const { textColor } = useContext(MenuBrandingContext)
  const { selectCategory } = useMenuExperienceCategoryNavigation()
  const { openRecipeDetail } = useMenuExperienceRecipeDetails()
  const shouldShowCaloriesTutorial = useShouldShowCaloriesTutorial(collectionId)
  const { setBrowserCTAVisible } = useSetBrowserCTAVisibility()
  const { getRecipeCustomisationData, openRecipeCustomisations, closeRecipeCustomisations } =
    useRecipeGridCustomisationData({ collectionId })
  const { recipeIdForEducation, closeEducation } = useRecipeGridCustomisationEducation(contentBlock)
  const {
    closeFlexPortionEditor,
    getFlexPortionData,
    openFlexPortionEditor,
    removeFlexRecipe,
    selectFlexPortions,
    updateFlexPortions,
  } = useRecipeGridFlex({ items: contentBlock.items })
  const { closeFlexEducationTooltip, flexEducationRecipeId, onRecipeAdded } =
    useRecipeGridFlexEducation()
  const { getRecipeTileState, onAddClick, onRemoveClick } = useRecipeGridBasket(
    contentBlock.items,
    { onRecipeAdded },
  )

  const renderFlexPortionBadge = (recipeId: RecipeUUID) => {
    const { isVisible, quantity, numPortions } = getFlexPortionData(recipeId)

    if (!isVisible) {
      return null
    }

    return (
      <FlexPortionTileBadge
        recipeCount={quantity}
        portionCount={numPortions}
        onEdit={() => openFlexPortionEditor(recipeId)}
        showEducationTooltip={flexEducationRecipeId === recipeId}
        onCloseEducationTooltip={closeFlexEducationTooltip}
      />
    )
  }

  const renderFlexPortionEditor = (recipeId: RecipeUUID) => {
    const { isEditorOpen, quantity, numPortions, portions } = getFlexPortionData(recipeId)

    if (!isEditorOpen) {
      return null
    }

    return (
      <FlexPortionTileEditor
        isOpen
        recipeCount={quantity}
        recipeId={recipeId}
        numPortions={numPortions}
        portions={portions}
        onClose={closeFlexPortionEditor}
        onRemoveRecipe={removeFlexRecipe}
        onUpdateRecipePortions={updateFlexPortions}
        onSelectPortions={(updatedNumPortions) => selectFlexPortions(recipeId, updatedNumPortions)}
      />
    )
  }

  const onRecipeClick = (recipeId: RecipeUUID, recipePosition: number) => {
    const recipe = contentBlock.items.find((item) => item.id === recipeId)

    if (!recipe) {
      return
    }

    openRecipeDetail({
      recipeId,
      title: recipe.title,
      position: recipePosition.toString(),
      source: 'recipe_grid',
    })
  }

  const onWantToSeeMoreClick = () => {
    selectCategory(
      {
        id: CollectionId.AllRecipes,
        slug: CollectionSlug.AllRecipes,
      },
      'recipe_grid',
    )
    dispatch(getClickAllRecipesTrackingAction())
  }

  const supplementaryItems = [
    ...(shouldShowCaloriesTutorial
      ? [
          {
            id: 'calories-tutorial',
            position: 5 as const,
            content: (
              <Box position={DisplayPosition.Relative} width="100%">
                <CaloriesTutorial />
              </Box>
            ),
          },
        ]
      : []),
    ...(collectionId === CollectionId.Recommendations
      ? [
          {
            id: 'want-to-see-more',
            position: 'end' as const,
            content: <WantToSeeMore onClick={onWantToSeeMoreClick} />,
          },
        ]
      : []),
  ]

  return (
    <RecipeGrid
      contentBlock={contentBlock}
      recipeCountTextColor={textColor}
      supplementaryItems={supplementaryItems}
      getRecipeTileState={getRecipeTileState}
      getRecipeCustomisationData={getRecipeCustomisationData}
      customisationEducationRecipeId={recipeIdForEducation}
      onRecipeClick={onRecipeClick}
      onAddClick={onAddClick}
      onRemoveClick={onRemoveClick}
      onBlockedAddAttempt={setBrowserCTAVisible}
      onCustomiseOpen={openRecipeCustomisations}
      onCustomiseClose={closeRecipeCustomisations}
      onCustomisationEducationClose={closeEducation}
      renderFlexPortionBadge={renderFlexPortionBadge}
      renderFlexPortionEditor={renderFlexPortionEditor}
    />
  )
}
